// src/Components/common/FeatureTour.js
//
// The guided walkthrough: a spotlight over one element at a time with a
// short card, Back / Next / Skip, and a step counter.
//
// WHEN IT RUNS. Mounted once in Routers. On every route change it looks up
// the page's tour (tourSteps.js), waits for that page's elements to appear,
// and starts if the signed-in user has not seen THIS VERSION of THIS tour.
// Skipping counts as seen - nobody is nagged twice. Pages without a tour do
// nothing at all, and the header hides its "Show me around" button there.
//
// HOW A STEP MOVES. Sliding the spotlight across the page while the page
// itself scrolls looked jittery, so a step change is a fade: the card and
// spotlight go transparent, the target is scrolled into view instantly, the
// geometry is measured once the layout has settled, then both fade back in
// at the new place. Position never animates; only opacity does.
//
// WHERE "SEEN" LIVES. saved-jobs/{uid}.featureTours = { [tourId]: version },
// the per-user document that already carries the selected job, so it needs
// no new security rule and follows the user across devices. A localStorage
// mirror answers instantly on the next load so nothing flashes.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import get from "lodash/get";

import { db } from "../../firebase";
import { useAuth } from "../../Contexts/AuthContext";
import constant from "../../utils/constant";
import { logFirebaseEvent } from "../../utils/analytics";
import { findTourForPath } from "../../utils/tour/tourSteps";
import "../../Styles/tour.css";

const { DB_NAME } = constant;

export const TOUR_REPLAY_EVENT = "careerclaw:start-tour";
const SAVED_JOBS_COLLECTION = DB_NAME.SAVED_JOBS || "saved-jobs";
const TOURS_FIELD = "featureTours";
const MIRROR_PREFIX = "featureTours:";

const WAIT_FOR_TARGETS_MS = 7000;
const POLL_MS = 300;
// Let the page finish its own first render (loaders, Firestore-driven
// sections) before the spotlight appears; a tour that starts on a page that
// is still moving is the flicker the user notices.
const START_DELAY_MS = 900;
const FADE_MS = 180;
const SPOTLIGHT_PADDING = 8;
const CARD_WIDTH = 320;
const CARD_GAP = 14;

// ---------------------------------------------------------------------------
// "seen" storage
// ---------------------------------------------------------------------------

const readMirror = (uid) => {
  try {
    const raw = localStorage.getItem(`${MIRROR_PREFIX}${uid}`);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

const writeMirror = (uid, seen) => {
  try {
    localStorage.setItem(`${MIRROR_PREFIX}${uid}`, JSON.stringify(seen));
  } catch (error) {
    // Private mode: the Firestore copy still holds it.
  }
};

const docRefFor = (uid) => db.collection(SAVED_JOBS_COLLECTION).doc(uid);

const hasSeen = (seen, tour) => Number(get(seen, [tour.id], 0)) >= tour.version;

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

const isVisible = (element) => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const measure = (element) => {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top - SPOTLIGHT_PADDING,
    left: rect.left - SPOTLIGHT_PADDING,
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2,
  };
};

// Card below the target when there is room, otherwise above; clamped so it
// never leaves the viewport on a phone.
const placeCard = (box, cardHeight) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = Math.min(CARD_WIDTH, viewportWidth - 24);

  const below = box.top + box.height + CARD_GAP;
  const fitsBelow = below + cardHeight <= viewportHeight - 12;
  const top = fitsBelow ? below : Math.max(12, box.top - CARD_GAP - cardHeight);

  let left = box.left + box.width / 2 - width / 2;
  left = Math.max(12, Math.min(left, viewportWidth - width - 12));

  return { top, left, width, arrow: fitsBelow ? "up" : "down" };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FeatureTour = () => {
  const { pathname } = useLocation();
  const { currentUser } = useAuth();
  const uid = get(currentUser, "uid", null);

  const [seen, setSeen] = useState({});
  const [seenLoaded, setSeenLoaded] = useState(false);
  const [active, setActive] = useState(null); // { id, version, title, steps }
  const [stepIndex, setStepIndex] = useState(0);
  // Geometry for the current step; null until measured, so nothing is drawn
  // in the wrong place first.
  const [layout, setLayout] = useState(null); // { box, card }
  const [isShown, setIsShown] = useState(false);
  const cardRef = useRef(null);
  const pollRef = useRef(null);
  const startedForPathRef = useRef("");

  // ---- load "seen" for this user
  useEffect(() => {
    setSeenLoaded(false);
    if (!uid) {
      setSeen({});
      setSeenLoaded(true);
      return;
    }

    const mirrored = readMirror(uid);
    setSeen(mirrored);

    docRefFor(uid)
      .get()
      .then((doc) => {
        const remote = get(doc.data(), TOURS_FIELD, {}) || {};
        const merged = { ...mirrored };
        Object.keys(remote).forEach((key) => {
          merged[key] = Math.max(Number(merged[key] || 0), Number(remote[key] || 0));
        });
        setSeen(merged);
        writeMirror(uid, merged);
      })
      .catch(() => {})
      .finally(() => setSeenLoaded(true));
  }, [uid]);

  const markSeen = useCallback(
    (tour) => {
      if (!uid || !tour) return;
      const next = { ...seen, [tour.id]: Math.max(Number(seen[tour.id] || 0), tour.version) };
      setSeen(next);
      writeMirror(uid, next);
      docRefFor(uid)
        .set({ [TOURS_FIELD]: next }, { merge: true })
        .catch(() => {});
    },
    [uid, seen]
  );

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Only steps whose element is on the page right now.
  const buildTour = useCallback(() => {
    const pageTour = findTourForPath(pathname);
    if (!pageTour) return null;
    const steps = pageTour.steps.filter((step) =>
      isVisible(document.querySelector(step.target))
    );
    return steps.length ? { ...pageTour, steps } : null;
  }, [pathname]);

  const start = useCallback((tour, reason) => {
    if (!tour) return;
    stopPolling();
    logFirebaseEvent("FEATURE_TOUR_START", { tour: tour.id, reason });
    setLayout(null);
    setIsShown(false);
    setStepIndex(0);
    setActive(tour);
  }, []);

  // ---- auto-start on route change, once per page visit
  useEffect(() => {
    setActive(null);
    setLayout(null);
    stopPolling();
    if (!uid || !seenLoaded) return undefined;
    if (startedForPathRef.current === pathname) return undefined;

    const pageTour = findTourForPath(pathname);
    if (!pageTour || hasSeen(seen, pageTour)) return undefined;

    const startedAt = Date.now();
    pollRef.current = setInterval(() => {
      const ready = pageTour.steps.some((step) =>
        isVisible(document.querySelector(step.target))
      );
      if (ready) {
        stopPolling();
        startedForPathRef.current = pathname;
        setTimeout(() => start(buildTour(), "auto"), START_DELAY_MS);
      } else if (Date.now() - startedAt > WAIT_FOR_TARGETS_MS) {
        stopPolling();
      }
    }, POLL_MS);

    return stopPolling;
  }, [pathname, uid, seenLoaded, seen, buildTour, start]);

  // ---- replay from the header button (the header hides it where no tour)
  useEffect(() => {
    const handler = () => {
      if (!uid) return;
      start(buildTour(), "replay");
    };
    window.addEventListener(TOUR_REPLAY_EVENT, handler);
    return () => window.removeEventListener(TOUR_REPLAY_EVENT, handler);
  }, [uid, buildTour, start]);

  // ---- move to the current step: fade out, scroll, measure, fade in
  const step = active ? active.steps[stepIndex] : null;

  useEffect(() => {
    if (!step) return undefined;

    const element = document.querySelector(step.target);
    if (!element) return undefined;

    let cancelled = false;
    let frame = 0;
    setIsShown(false);

    const measureNow = () => {
      if (cancelled) return;
      const box = measure(element);
      const cardHeight = cardRef.current ? cardRef.current.offsetHeight : 170;
      setLayout({ box, card: placeCard(box, cardHeight) });
    };

    // Instant scroll (no smooth scrolling under a fixed overlay - that is the
    // jitter), then two frames for layout to settle, then measure and show.
    const fadeOut = setTimeout(() => {
      element.scrollIntoView({ behavior: "auto", block: "center", inline: "nearest" });
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          measureNow();
          // The card's height is only known once it has content; measure a
          // second time so the placement uses the real height, then show.
          frame = requestAnimationFrame(() => {
            measureNow();
            if (!cancelled) setIsShown(true);
          });
        });
      });
    }, layout ? FADE_MS : 0);

    // Keep the spotlight on the element if the user scrolls or resizes, with
    // no animation so it tracks exactly.
    let raf = 0;
    const follow = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measureNow);
    };
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);

    return () => {
      cancelled = true;
      clearTimeout(fadeOut);
      cancelAnimationFrame(frame);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
    // eslint-disable-next-line
  }, [step]);

  const finish = useCallback(
    (how) => {
      if (!active) return;
      logFirebaseEvent(`FEATURE_TOUR_${how}`, {
        tour: active.id,
        step: stepIndex + 1,
        of: active.steps.length,
      });
      markSeen(active);
      setIsShown(false);
      setTimeout(() => {
        setActive(null);
        setLayout(null);
      }, FADE_MS);
    },
    [active, stepIndex, markSeen]
  );

  const goTo = (index) => {
    setIsShown(false);
    setStepIndex(index);
  };

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") finish("SKIP");
      if (event.key === "ArrowRight" || event.key === "Enter") {
        if (stepIndex < active.steps.length - 1) goTo(stepIndex + 1);
        else finish("DONE");
      }
      if (event.key === "ArrowLeft" && stepIndex > 0) goTo(stepIndex - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, stepIndex, finish]);

  if (!active || !step) return null;

  const isLast = stepIndex === active.steps.length - 1;
  const box = layout ? layout.box : null;
  const card = layout ? layout.card : { top: 0, left: 0, width: CARD_WIDTH, arrow: "up" };

  return (
    <div
      className={`tour-root ${isShown ? "is-shown" : ""}`}
      role="dialog"
      aria-modal="false"
      aria-label={active.title}
    >
      <div className="tour-backdrop" onClick={() => finish("SKIP")} />
      {box && (
        <div
          className="tour-spotlight"
          style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
        />
      )}

      <div
        ref={cardRef}
        className={`tour-card is-arrow-${card.arrow}`}
        style={{ top: card.top, left: card.left, width: card.width }}
      >
        <div className="tour-card-head">
          <span className="tour-card-kicker">{active.title}</span>
          <span className="tour-card-count">
            {stepIndex + 1} of {active.steps.length}
          </span>
        </div>
        <div className="tour-card-title">{step.title}</div>
        <div className="tour-card-body">{step.body}</div>
        <div className="tour-card-actions">
          <button
            type="button"
            className="btn btn-sm btn-link p-0 tour-skip"
            onClick={() => finish("SKIP")}
          >
            Skip
          </button>
          <span className="d-inline-flex gap-2">
            {stepIndex > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => goTo(stepIndex - 1)}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => (isLast ? finish("DONE") : goTo(stepIndex + 1))}
            >
              {isLast ? "Done" : "Next"}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

/** Fire from anywhere (the header button) to replay the current page's tour. */
export const startFeatureTour = () => {
  window.dispatchEvent(new CustomEvent(TOUR_REPLAY_EVENT));
};

export default FeatureTour;
